import { useEffect, useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { CalendarDays, Loader2 } from 'lucide-react';
import { useSeo } from '@/lib/seo';
import { formatInsightDate, topicFor } from '@/data/insights';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Secure draft preview. Requires a short-lived HMAC token minted by an
// authenticated admin; always noindex; renders only the requested record.
export default function PreviewPage() {
  const { resource, slug } = useParams();
  const [searchParams] = useSearchParams();
  const [state, setState] = useState({ loading: true, item: null, error: '' });

  useSeo({ title: 'Content preview | Amden Group', robots: 'noindex, nofollow' });

  useEffect(() => {
    fetch(`${API}/public/preview/${resource}/${slug}?token=${encodeURIComponent(searchParams.get('token') || '')}`)
      .then(async (res) => {
        if (!res.ok) throw new Error(res.status === 403 ? 'This preview link is invalid or has expired.' : 'Content not found.');
        return res.json();
      })
      .then((data) => setState({ loading: false, item: data.item, error: '' }))
      .catch((err) => setState({ loading: false, item: null, error: err.message }));
  }, [resource, slug, searchParams]);

  const { loading, item, error } = state;

  return (
    <div className="min-h-screen bg-mist" data-testid="preview-page">
      <div className="bg-amber-100 px-4 py-2.5 text-center text-xs font-bold text-amber-900">
        Draft preview — this is how the content will look when published. Not public.
      </div>
      <div className="mx-auto max-w-3xl px-4 py-12">
        {loading && (
          <div className="flex justify-center py-16" role="status">
            <Loader2 className="h-6 w-6 animate-spin text-navy-900" />
          </div>
        )}
        {error && (
          <div className="rounded-xl border border-red-200 bg-white p-8 text-center" role="alert">
            <p className="text-sm font-semibold text-red-700">{error}</p>
            <Link to="/admin" className="mt-4 inline-block text-sm font-bold text-navy-900 underline-offset-2 hover:underline">
              Back to admin
            </Link>
          </div>
        )}
        {item && (
          <article className="rounded-2xl border border-slate-200 bg-white p-8 sm:p-10">
            <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-400">
              {resource} · {item.status}
            </p>
            <h1 className="mt-3 text-balance text-3xl font-extrabold tracking-tight text-navy-900 sm:text-4xl">
              {item.title || item.name || item.question}
            </h1>
            {item.publishedAt && (
              <p className="mt-3 inline-flex items-center gap-1.5 text-sm text-slate-500">
                <CalendarDays className="h-4 w-4" aria-hidden="true" />
                {formatInsightDate(item.publishedAt)}
              </p>
            )}
            {item.primaryTopic && topicFor(item.primaryTopic) && (
              <p className="mt-2 text-xs font-bold uppercase tracking-wide" style={{ color: topicFor(item.primaryTopic).dark }}>
                {topicFor(item.primaryTopic).label}
              </p>
            )}
            {(item.excerpt || item.summary || item.shortDescription || item.answer) && (
              <p className="mt-5 border-l-2 border-gold-500 pl-4 text-lg font-medium leading-relaxed text-navy-800">
                {item.excerpt || item.summary || item.shortDescription || item.answer}
              </p>
            )}
            {Array.isArray(item.body) && item.body.length > 0 && (
              <div className="mt-8 space-y-5 text-base leading-relaxed text-slate-700">
                {item.body.map((b, i) => {
                  if (b.type === 'h2') return <h2 key={i} className="pt-3 text-2xl font-extrabold tracking-tight text-navy-900">{b.text}</h2>;
                  if (b.type === 'h3') return <h3 key={i} className="pt-2 text-xl font-extrabold tracking-tight text-navy-900">{b.text}</h3>;
                  if (b.type === 'quote') return <blockquote key={i} className="border-l-2 border-gold-500 pl-4 italic text-navy-800">{b.text}</blockquote>;
                  if (b.type === 'ul') return <ul key={i} className="list-disc space-y-1.5 pl-6">{(b.items || []).map((li, j) => <li key={j}>{li}</li>)}</ul>;
                  return <p key={i}>{b.text}</p>;
                })}
              </div>
            )}
            {item.description && !item.body && (
              <p className="mt-6 text-base leading-relaxed text-slate-700">{item.description}</p>
            )}
          </article>
        )}
      </div>
    </div>
  );
}
